import { ArrowLeftIcon, Button, IconSize, Select, SelectItem, Tooltip } from '@protoss/ui';
import cronstrue from 'cronstrue';
import * as React from 'react';
import { PageErrorState } from '../../../components/shared/PageErrorState';
import { getLocalTimezoneAbbr } from '../../../lib/datetime';
import { useSourceSystemSettings } from '../hooks/useSourceSystemSettings';
import { sourceSystemApi } from '../services/sourceSystemApi';
import type {
  BranchOption,
  PipelineCategory,
  PipelineIntegrationOption,
  PipelineOption,
  RepoIntegrationOption,
  RepoOption,
  SourceSystemSettingsPipelineEntry,
  SourceSystemSettingsRepoEntry,
  SourceType,
} from '../types/source-system';

export interface SourceSystemSettingsPaneHandle {
  enterEdit: () => void;
  cancelEdit: () => void;
}

interface SourceSystemSettingsPaneProps {
  projectUuid: string;
  sourceType: SourceType;
  /** Called after a successful settings save so the main panel can re-fetch. */
  onSaved?: () => void;
  /** Called whenever edit mode turns on (true) or off (false). */
  onEditModeChange?: (editing: boolean) => void;
}

/** An in-progress or committed repo row being edited inline in the settings pane. */
interface EditableRepoRow {
  /** Ephemeral stable key for React list management */
  _key: string;
  /** UUID of the project_integration binding - '' until the user selects one */
  projectIntegrationUuid: string;
  /** Provider-native repo ID - '' until the user selects one */
  providerResourceId: string;
  providerResourceName: string;
  /** Parent ID (ADO project ID for hierarchical providers) */
  parentId: string | null;
  /** Branch name - '' until the user selects one */
  selectedBranch: string;
  /** Whether this repo is enabled for sync */
  enabled: boolean;
  /** Repos loaded for the chosen integration - populated after integration is selected */
  repos: RepoOption[];
  reposLoading: boolean;
  /** Branches loaded for the chosen repo - populated after repo is selected */
  branches: BranchOption[];
  branchesLoading: boolean;
}

let _keyCounter = 0;
function nextKey() {
  return `row-${++_keyCounter}`;
}

function rowFromEntry(entry: SourceSystemSettingsRepoEntry): EditableRepoRow {
  return {
    _key: nextKey(),
    projectIntegrationUuid: entry.projectIntegrationUuid,
    providerResourceId: entry.providerResourceId,
    providerResourceName: entry.providerResourceName,
    parentId: null,
    selectedBranch: entry.selectedBranch ?? '',
    enabled: entry.enabled,
    repos: [],
    reposLoading: false,
    branches: [],
    branchesLoading: false,
  };
}

/** An in-progress or committed pipeline row being edited inline in the settings pane. */
interface EditablePipelineRow {
  _key: string;
  pipelineCategory: PipelineCategory;
  projectIntegrationUuid: string;
  providerResourceId: string;
  providerResourceName: string;
  enabled: boolean;
  pipelines: PipelineOption[];
  pipelinesLoading: boolean;
}

function rowFromPipelineEntry(entry: SourceSystemSettingsPipelineEntry): EditablePipelineRow {
  return {
    _key: nextKey(),
    pipelineCategory: entry.pipelineCategory,
    projectIntegrationUuid: entry.projectIntegrationUuid,
    providerResourceId: entry.providerResourceId,
    providerResourceName: entry.providerResourceName,
    enabled: entry.enabled,
    pipelines: [],
    pipelinesLoading: false,
  };
}

function formatIntegrationOptionLabel(
  integration: RepoIntegrationOption | PipelineIntegrationOption
): string {
  return [integration.displayName, integration.projectName, integration.providerName]
    .filter(Boolean)
    .join(' | ');
}

/**
 * Convert a standard 5-field cron (UTC) to its local-time equivalent.
 * Only works when the minute and hour fields are plain integers (no wildcards/ranges).
 * Returns null for complex expressions - the caller should fall back to showing UTC.
 */
function localCronDesc(utcCron: string): string | null {
  const parts = utcCron.trim().split(/\s+/);
  if (parts.length < 5) return null;

  const minuteStr = parts[0];
  const hourStr = parts[1];
  const minute = Number(minuteStr);
  const hour = Number(hourStr);

  // Reject non-integer fields (wildcards, ranges, step values, lists)
  if (!Number.isInteger(minute) || !Number.isInteger(hour)) return null;
  if (minuteStr !== String(minute) || hourStr !== String(hour)) return null;

  // Build a UTC date at this hour:minute, then read back in local time
  const ref = new Date();
  ref.setUTCHours(hour, minute, 0, 0);
  const localMinute = ref.getMinutes();
  const localHour = ref.getHours();

  // If the hour didn't change there's no point showing a duplicate line
  if (localHour === hour && localMinute === minute) return null;

  const localCron = [String(localMinute), String(localHour), ...parts.slice(2)].join(' ');
  try {
    return cronstrue.toString(localCron, { throwExceptionOnParseError: true });
  } catch {
    return null;
  }
}

function CronPreview({ cron }: { cron: string }) {
  const trimmed = cron.trim();
  if (!trimmed) return null;
  try {
    const utcDesc = cronstrue.toString(trimmed, { throwExceptionOnParseError: true });
    const localDesc = localCronDesc(trimmed);
    const tzAbbr = getLocalTimezoneAbbr();
    return (
      <div className="ss-cron-preview">
        <div className="ss-cron-preview__row">
          <span className="ss-cron-preview__label">UTC</span>
          <span className="ss-cron-preview__desc">{utcDesc}</span>
        </div>
        {localDesc && (
          <div className="ss-cron-preview__row">
            <span className="ss-cron-preview__label">
              Local
              {tzAbbr && <span className="ss-cron-preview__tz">{tzAbbr}</span>}
            </span>
            <span className="ss-cron-preview__desc">{localDesc}</span>
          </div>
        )}
      </div>
    );
  } catch {
    return <p className="ss-cron-preview ss-cron-preview--error">Invalid cron expression</p>;
  }
}

/**
 * Right-pane settings panel for a source system (repos, boards, pipelines).
 *
 * Renders in two modes:
 *   view - read-only summary of repos and schedule (all roles)
 *   edit - full repo management + schedule editing (Admin/Manager only)
 *
 * IMPORTANT: Repo selection is inline - no modals or dialogs are used.
 * Each row exposes three dependent Radix Select fields: Integration → Repository → Branch.
 */
export const SourceSystemSettingsPane = React.forwardRef<
  SourceSystemSettingsPaneHandle,
  SourceSystemSettingsPaneProps
>(function SourceSystemSettingsPane({ projectUuid, sourceType, onSaved, onEditModeChange }, ref) {
  const { settings, loading, saving, error, save, reload } = useSourceSystemSettings(
    projectUuid,
    sourceType
  );

  const [editMode, setEditMode] = React.useState(false);

  // Local editable state - repos
  const [editRows, setEditRows] = React.useState<EditableRepoRow[]>([]);
  const [rowError, setRowError] = React.useState<string | null>(null);
  const [editScheduleEnabled, setEditScheduleEnabled] = React.useState(false);
  const [editScheduleCron, setEditScheduleCron] = React.useState('');

  // Integration options - fetched once when entering edit mode (repos)
  const [integrations, setIntegrations] = React.useState<RepoIntegrationOption[]>([]);
  const [integrationsLoading, setIntegrationsLoading] = React.useState(false);

  // Local editable state - pipelines
  const [buildRows, setBuildRows] = React.useState<EditablePipelineRow[]>([]);
  const [releaseRows, setReleaseRows] = React.useState<EditablePipelineRow[]>([]);
  const [pipelineRowError, setPipelineRowError] = React.useState<string | null>(null);

  // Integration options for pipelines - same bindings but typed for pipelines
  const [pipelineIntegrations, setPipelineIntegrations] = React.useState<
    PipelineIntegrationOption[]
  >([]);
  const [pipelineIntegrationsLoading, setPipelineIntegrationsLoading] = React.useState(false);

  // Expose enterEdit imperatively so the rail pane header can trigger it
  React.useImperativeHandle(ref, () => ({ enterEdit, cancelEdit }));

  // ── Edit mode entry ─────────────────────────────────────────────────────

  function enterEdit() {
    if (!settings) return;
    const rows = settings.repos ? settings.repos.map((entry) => rowFromEntry(entry)) : [];
    setEditRows(rows);
    setEditScheduleEnabled(settings.schedule.enabled);
    setEditScheduleCron(settings.schedule.scheduleCron ?? '');
    setRowError(null);
    setPipelineRowError(null);
    setEditMode(true);
    onEditModeChange?.(true);

    if (sourceType === 'repo') {
      // Fetch integration options concurrently with per-row repo/branch hydration
      setIntegrationsLoading(true);
      void sourceSystemApi
        .getRepoIntegrations(projectUuid)
        .then((res) => {
          setIntegrations(res.integrations);

          // For each existing row, pre-fetch repos and branches so selects are populated
          rows.forEach((row) => {
            if (!row.projectIntegrationUuid) return;

            // Mark reposLoading for this row
            setEditRows((prev) =>
              prev.map((r) => (r._key === row._key ? { ...r, reposLoading: true } : r))
            );

            void sourceSystemApi
              .getReposForIntegration(projectUuid, row.projectIntegrationUuid)
              .then((repoRes) => {
                setEditRows((prev) =>
                  prev.map((r) =>
                    r._key === row._key ? { ...r, repos: repoRes.repos, reposLoading: false } : r
                  )
                );

                if (!row.providerResourceId) return;

                // Determine parentId from the repo list
                const repoParentId =
                  repoRes.repos.find((rp) => rp.id === row.providerResourceId)?.parentId ?? null;

                setEditRows((prev) =>
                  prev.map((r) =>
                    r._key === row._key
                      ? { ...r, parentId: repoParentId, branchesLoading: true }
                      : r
                  )
                );

                void sourceSystemApi
                  .getBranchesForRepo(
                    projectUuid,
                    row.projectIntegrationUuid,
                    row.providerResourceId,
                    repoParentId
                  )
                  .then((branchRes) => {
                    setEditRows((prev) =>
                      prev.map((r) =>
                        r._key === row._key
                          ? { ...r, branches: branchRes.branches, branchesLoading: false }
                          : r
                      )
                    );
                  })
                  .catch(() => {
                    setEditRows((prev) =>
                      prev.map((r) =>
                        r._key === row._key ? { ...r, branches: [], branchesLoading: false } : r
                      )
                    );
                  });
              })
              .catch(() => {
                setEditRows((prev) =>
                  prev.map((r) =>
                    r._key === row._key ? { ...r, repos: [], reposLoading: false } : r
                  )
                );
              });
          });
        })
        .catch(() => setIntegrations([]))
        .finally(() => setIntegrationsLoading(false));
    }

    if (sourceType === 'pipeline') {
      const initialBuildRows = (settings.buildPipelines ?? []).map(rowFromPipelineEntry);
      const initialReleaseRows = (settings.releasePipelines ?? []).map(rowFromPipelineEntry);
      setBuildRows(initialBuildRows);
      setReleaseRows(initialReleaseRows);

      setPipelineIntegrationsLoading(true);
      void sourceSystemApi
        .getPipelineIntegrations(projectUuid)
        .then((res) => {
          setPipelineIntegrations(res.integrations);

          // Pre-fetch pipeline lists for existing rows
          const hydrateRows = (
            rows: EditablePipelineRow[],
            setRows: React.Dispatch<React.SetStateAction<EditablePipelineRow[]>>
          ) => {
            rows.forEach((row) => {
              if (!row.projectIntegrationUuid) return;
              setRows((prev) =>
                prev.map((r) => (r._key === row._key ? { ...r, pipelinesLoading: true } : r))
              );
              void sourceSystemApi
                .getPipelinesForIntegration(
                  projectUuid,
                  row.projectIntegrationUuid,
                  row.pipelineCategory
                )
                .then((plRes) => {
                  setRows((prev) =>
                    prev.map((r) =>
                      r._key === row._key
                        ? { ...r, pipelines: plRes.pipelines, pipelinesLoading: false }
                        : r
                    )
                  );
                })
                .catch(() => {
                  setRows((prev) =>
                    prev.map((r) =>
                      r._key === row._key ? { ...r, pipelines: [], pipelinesLoading: false } : r
                    )
                  );
                });
            });
          };

          hydrateRows(initialBuildRows, setBuildRows);
          hydrateRows(initialReleaseRows, setReleaseRows);
        })
        .catch(() => setPipelineIntegrations([]))
        .finally(() => setPipelineIntegrationsLoading(false));
    }
  }

  function cancelEdit() {
    setEditMode(false);
    setRowError(null);
    setPipelineRowError(null);
    onEditModeChange?.(false);
  }

  // ── Row management ─────────────────────────────────────────────────────

  function addNewRow() {
    setEditRows((prev) => [
      ...prev,
      {
        _key: nextKey(),
        projectIntegrationUuid: '',
        providerResourceId: '',
        providerResourceName: '',
        parentId: null,
        selectedBranch: '',
        enabled: true,
        repos: [],
        reposLoading: false,
        branches: [],
        branchesLoading: false,
      },
    ]);
    setRowError(null);
  }

  function removeRow(key: string) {
    setEditRows((prev) => prev.filter((r) => r._key !== key));
    setRowError(null);
  }

  // ── Dependent select handlers ──────────────────────────────────────────

  function handleIntegrationChange(key: string, integrationUuid: string) {
    setEditRows((prev) =>
      prev.map((r) => {
        if (r._key !== key) return r;
        return {
          ...r,
          projectIntegrationUuid: integrationUuid,
          // Downstream resets
          providerResourceId: '',
          providerResourceName: '',
          parentId: null,
          selectedBranch: '',
          repos: [],
          reposLoading: true,
          branches: [],
          branchesLoading: false,
        };
      })
    );

    void sourceSystemApi
      .getReposForIntegration(projectUuid, integrationUuid)
      .then((res) => {
        setEditRows((prev) =>
          prev.map((r) =>
            r._key === key && r.projectIntegrationUuid === integrationUuid
              ? { ...r, repos: res.repos, reposLoading: false }
              : r
          )
        );
      })
      .catch((err: unknown) => {
        void err;
        setEditRows((prev) =>
          prev.map((r) => (r._key === key ? { ...r, repos: [], reposLoading: false } : r))
        );
      });
  }

  function handleRepoChange(key: string, repoId: string) {
    setEditRows((prev) => {
      const row = prev.find((r) => r._key === key);
      if (!row) return prev;
      const repo = row.repos.find((r) => r.id === repoId);
      return prev.map((r) => {
        if (r._key !== key) return r;
        return {
          ...r,
          providerResourceId: repoId,
          providerResourceName: repo?.name ?? repoId,
          parentId: repo?.parentId ?? null,
          // Downstream resets
          selectedBranch: '',
          branches: [],
          branchesLoading: true,
        };
      });
    });

    // Resolve parentId for the branch fetch (need updated row state)
    setEditRows((prev) => {
      const row = prev.find((r) => r._key === key);
      if (!row) return prev;
      const bindingUuid = row.projectIntegrationUuid;
      const parentId = row.repos.find((r) => r.id === repoId)?.parentId ?? null;

      void sourceSystemApi
        .getBranchesForRepo(projectUuid, bindingUuid, repoId, parentId)
        .then((res) => {
          setEditRows((cur) =>
            cur.map((r) =>
              r._key === key && r.providerResourceId === repoId
                ? { ...r, branches: res.branches, branchesLoading: false }
                : r
            )
          );
        })
        .catch(() => {
          setEditRows((cur) =>
            cur.map((r) => (r._key === key ? { ...r, branches: [], branchesLoading: false } : r))
          );
        });

      return prev;
    });
  }

  function handleBranchChange(key: string, branchName: string) {
    setEditRows((prev) =>
      prev.map((r) => (r._key === key ? { ...r, selectedBranch: branchName } : r))
    );
  }

  function updateRepoEnabled(key: string, enabled: boolean) {
    setEditRows((prev) => prev.map((r) => (r._key === key ? { ...r, enabled } : r)));
  }

  // ── Pipeline row management ─────────────────────────────────────────────

  function addNewPipelineRow(category: PipelineCategory) {
    const setRows = category === 'build' ? setBuildRows : setReleaseRows;
    setRows((prev) => [
      ...prev,
      {
        _key: nextKey(),
        pipelineCategory: category,
        projectIntegrationUuid: '',
        providerResourceId: '',
        providerResourceName: '',
        enabled: true,
        pipelines: [],
        pipelinesLoading: false,
      },
    ]);
    setPipelineRowError(null);
  }

  function removePipelineRow(key: string, category: PipelineCategory) {
    const setRows = category === 'build' ? setBuildRows : setReleaseRows;
    setRows((prev) => prev.filter((r) => r._key !== key));
    setPipelineRowError(null);
  }

  function handlePipelineIntegrationChange(
    key: string,
    category: PipelineCategory,
    integrationUuid: string
  ) {
    const setRows = category === 'build' ? setBuildRows : setReleaseRows;
    setRows((prev) =>
      prev.map((r) => {
        if (r._key !== key) return r;
        return {
          ...r,
          projectIntegrationUuid: integrationUuid,
          providerResourceId: '',
          providerResourceName: '',
          pipelines: [],
          pipelinesLoading: true,
        };
      })
    );

    void sourceSystemApi
      .getPipelinesForIntegration(projectUuid, integrationUuid, category)
      .then((res) => {
        setRows((prev) =>
          prev.map((r) =>
            r._key === key && r.projectIntegrationUuid === integrationUuid
              ? { ...r, pipelines: res.pipelines, pipelinesLoading: false }
              : r
          )
        );
      })
      .catch(() => {
        setRows((prev) =>
          prev.map((r) => (r._key === key ? { ...r, pipelines: [], pipelinesLoading: false } : r))
        );
      });
  }

  function handlePipelineChange(key: string, category: PipelineCategory, pipelineId: string) {
    const setRows = category === 'build' ? setBuildRows : setReleaseRows;
    setRows((prev) => {
      const row = prev.find((r) => r._key === key);
      const pipeline = row?.pipelines.find((p) => p.id === pipelineId);
      return prev.map((r) =>
        r._key === key
          ? {
              ...r,
              providerResourceId: pipelineId,
              providerResourceName: pipeline?.name ?? pipelineId,
            }
          : r
      );
    });
  }

  function updatePipelineEnabled(key: string, category: PipelineCategory, enabled: boolean) {
    const setRows = category === 'build' ? setBuildRows : setReleaseRows;
    setRows((prev) => prev.map((r) => (r._key === key ? { ...r, enabled } : r)));
  }

  // ── Save ───────────────────────────────────────────────────────────────

  async function handleSave() {
    setRowError(null);
    setPipelineRowError(null);

    if (sourceType === 'repo') {
      // Validate all rows are fully populated
      const incomplete = editRows.find(
        (r) => !r.projectIntegrationUuid || !r.providerResourceId || !r.selectedBranch
      );
      if (incomplete) {
        setRowError('All rows must have an integration, repository, and branch selected.');
        return;
      }

      // Duplicate detection
      const seen = new Set<string>();
      for (const r of editRows) {
        const key = `${r.projectIntegrationUuid}|${r.providerResourceId}`;
        if (seen.has(key)) {
          setRowError(
            'Duplicate repository entries detected. Each repository can only be added once per integration.'
          );
          return;
        }
        seen.add(key);
      }

      const ok = await save({
        repos: editRows.map((r) => ({
          projectIntegrationUuid: r.projectIntegrationUuid,
          providerResourceId: r.providerResourceId,
          providerResourceName: r.providerResourceName,
          parentProviderResourceId: r.parentId,
          selectedBranch: r.selectedBranch,
          enabled: r.enabled,
        })),
        schedule: {
          enabled: editScheduleEnabled,
          scheduleCron: editScheduleEnabled ? editScheduleCron.trim() || null : null,
        },
      });

      if (ok) {
        setEditMode(false);
        onEditModeChange?.(false);
        onSaved?.();
      }
      return;
    }

    if (sourceType === 'pipeline') {
      const allPipelineRows = [...buildRows, ...releaseRows];
      const incomplete = allPipelineRows.find(
        (r) => !r.projectIntegrationUuid || !r.providerResourceId
      );
      if (incomplete) {
        setPipelineRowError('All rows must have an integration and pipeline selected.');
        return;
      }

      // Duplicate detection per category
      const seenBuild = new Set<string>();
      for (const r of buildRows) {
        const k = `${r.projectIntegrationUuid}|${r.providerResourceId}`;
        if (seenBuild.has(k)) {
          setPipelineRowError('Duplicate build pipeline entries detected.');
          return;
        }
        seenBuild.add(k);
      }
      const seenRelease = new Set<string>();
      for (const r of releaseRows) {
        const k = `${r.projectIntegrationUuid}|${r.providerResourceId}`;
        if (seenRelease.has(k)) {
          setPipelineRowError('Duplicate release pipeline entries detected.');
          return;
        }
        seenRelease.add(k);
      }

      const ok = await save({
        pipelines: allPipelineRows.map((r) => ({
          projectIntegrationUuid: r.projectIntegrationUuid,
          providerResourceId: r.providerResourceId,
          providerResourceName: r.providerResourceName,
          pipelineCategory: r.pipelineCategory,
          enabled: r.enabled,
        })),
        schedule: {
          enabled: editScheduleEnabled,
          scheduleCron: editScheduleEnabled ? editScheduleCron.trim() || null : null,
        },
      });

      if (ok) {
        setEditMode(false);
        onEditModeChange?.(false);
        onSaved?.();
      }
      return;
    }

    // Fallback for other source types (boards etc.) - only save schedule
    const ok = await save({
      schedule: {
        enabled: editScheduleEnabled,
        scheduleCron: editScheduleEnabled ? editScheduleCron.trim() || null : null,
      },
    });

    if (ok) {
      setEditMode(false);
      onEditModeChange?.(false);
      onSaved?.();
    }
  }

  // ── Render helpers ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="ss-settings-pane">
        <p className="ss-settings-pane__loading">Loading settings…</p>
      </div>
    );
  }

  if (error && !settings) {
    return (
      <div className="ss-settings-pane">
        <PageErrorState message={error} onRetry={() => void reload()} />
      </div>
    );
  }

  if (!settings || !settings.hasBinding) {
    return (
      <div className="ss-settings-pane">
        <p className="ss-settings-pane__hint">
          No integration connected. Go to <strong>Project › Integrations</strong> to connect a
          provider.
        </p>
      </div>
    );
  }

  return (
    <div className="ss-settings-pane">
      {error && (
        <p className="ss-settings-pane__error" role="alert">
          {error}
        </p>
      )}

      {/* ── Repositories section ── */}
      {sourceType === 'repo' && (
        <section className="ss-settings-view" aria-labelledby="ss-settings-repos-heading">
          <div className="ss-settings-section__header-row">
            <h3 className="ss-settings-section__title" id="ss-settings-repos-heading">
              Repositories
            </h3>
            {editMode && (
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={addNewRow}
                aria-label="Add repository"
              >
                Add
              </Button>
            )}
          </div>

          {rowError && (
            <p className="ss-settings-pane__error ss-settings-pane__error--inline" role="alert">
              {rowError}
            </p>
          )}

          {editMode && editRows.length === 0 && (
            <p className="ss-settings-pane__hint">
              No repositories added. Click <strong>Add</strong> to add one.
            </p>
          )}

          {!editMode && (!settings.repos || settings.repos.length === 0) && (
            <p className="ss-settings-pane__hint">No repositories selected.</p>
          )}

          {/* ── Edit mode: inline rows with 3 dependent selects each ── */}
          {editMode && editRows.length > 0 && (
            <div className="ss-repo-edit-list">
              {editRows.map((row) => (
                <div key={row._key} className="ss-repo-edit-row">
                  <div className="ss-repo-edit-row__fields">
                    {/* Integration select */}
                    <div className="ss-repo-edit-row__field">
                      <label className="ss-repo-edit-row__label">Integration</label>
                      <Select
                        value={row.projectIntegrationUuid || undefined}
                        onValueChange={(v) => handleIntegrationChange(row._key, v)}
                        placeholder={integrationsLoading ? 'Loading…' : 'Select integration'}
                        disabled={integrationsLoading}
                      >
                        {integrations.map((intg) => (
                          <SelectItem key={intg.uuid} value={intg.uuid}>
                            {formatIntegrationOptionLabel(intg)}
                          </SelectItem>
                        ))}
                      </Select>
                    </div>

                    {/* Repository select */}
                    <div className="ss-repo-edit-row__field">
                      <label className="ss-repo-edit-row__label">Repository</label>
                      <Select
                        key={`repo-sel-${row._key}-${row.projectIntegrationUuid}`}
                        value={row.providerResourceId || undefined}
                        onValueChange={(v) => handleRepoChange(row._key, v)}
                        placeholder={
                          !row.projectIntegrationUuid
                            ? 'Select integration first'
                            : row.reposLoading
                              ? 'Loading…'
                              : 'Select repository'
                        }
                        disabled={!row.projectIntegrationUuid || row.reposLoading}
                      >
                        {row.repos.map((repo) => (
                          <SelectItem key={repo.id} value={repo.id}>
                            {repo.name}
                          </SelectItem>
                        ))}
                      </Select>
                    </div>

                    {/* Branch select */}
                    <div className="ss-repo-edit-row__field">
                      <label className="ss-repo-edit-row__label">Branch</label>
                      <Select
                        key={`branch-sel-${row._key}-${row.providerResourceId}`}
                        value={row.selectedBranch || undefined}
                        onValueChange={(v) => handleBranchChange(row._key, v)}
                        placeholder={
                          !row.providerResourceId
                            ? 'Select repository first'
                            : row.branchesLoading
                              ? 'Loading…'
                              : 'Select branch'
                        }
                        disabled={!row.providerResourceId || row.branchesLoading}
                      >
                        {row.branches.map((branch) => (
                          <SelectItem key={branch.name} value={branch.name}>
                            {branch.name}
                          </SelectItem>
                        ))}
                      </Select>
                    </div>
                  </div>

                  <div className="ss-repo-edit-row__meta">
                    <label className="ss-repo-edit-row__enabled-label">
                      <input
                        type="checkbox"
                        checked={row.enabled}
                        onChange={(e) => updateRepoEnabled(row._key, e.target.checked)}
                        aria-label="Enable sync for this repository"
                      />
                      Active
                    </label>
                    <Tooltip.Simple label="Remove">
                      <button
                        type="button"
                        className="ss-repo-edit-row__remove"
                        onClick={() => removeRow(row._key)}
                        aria-label="Remove this repository"
                      >
                        ✕
                      </button>
                    </Tooltip.Simple>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── View mode: vertical label+value repo cards ── */}
          {!editMode && settings.repos && settings.repos.length > 0 && (
            <div className="ss-settings-repo-cards">
              {settings.repos.map((repo) => (
                <div
                  key={`${repo.projectIntegrationUuid}-${repo.providerResourceId}`}
                  className="ss-settings-repo-card"
                >
                  <div className="ss-meta-group">
                    <span className="ss-meta-label">Repository</span>
                    <Tooltip.Simple label={repo.providerResourceName}>
                      <span className="ss-meta-value ss-settings-repo-card__name">
                        {repo.providerResourceName}
                      </span>
                    </Tooltip.Simple>
                  </div>
                  <div className="ss-meta-group">
                    <span className="ss-meta-label">Branch</span>
                    <span className="ss-meta-value ss-settings-repo-card__branch">
                      {repo.selectedBranch ?? (
                        <em className="ss-settings-pane__hint--em">default</em>
                      )}
                    </span>
                  </div>
                  <div className="ss-meta-group">
                    <span className="ss-meta-label">Status</span>
                    <span
                      className={`ss-settings-repo-row__status ${repo.enabled ? 'ss-settings-repo-row__status--on' : 'ss-settings-repo-row__status--off'}`}
                    >
                      {repo.enabled ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ── Build Pipelines section ── */}
      {sourceType === 'pipeline' && (
        <section className="ss-settings-view" aria-labelledby="ss-settings-build-pipelines-heading">
          <div className="ss-settings-section__header-row">
            <h3 className="ss-settings-section__title" id="ss-settings-build-pipelines-heading">
              Build Pipelines
            </h3>
            {editMode && (
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => addNewPipelineRow('build')}
                aria-label="Add build pipeline"
              >
                Add
              </Button>
            )}
          </div>

          {pipelineRowError && (
            <p className="ss-settings-pane__error ss-settings-pane__error--inline" role="alert">
              {pipelineRowError}
            </p>
          )}

          {editMode && buildRows.length === 0 && (
            <p className="ss-settings-pane__hint">
              No build pipelines added. Click <strong>Add</strong> to add one.
            </p>
          )}

          {!editMode && (!settings.buildPipelines || settings.buildPipelines.length === 0) && (
            <p className="ss-settings-pane__hint">No build pipelines selected.</p>
          )}

          {editMode && buildRows.length > 0 && (
            <div className="ss-repo-edit-list">
              {buildRows.map((row) => (
                <div key={row._key} className="ss-repo-edit-row">
                  <div className="ss-repo-edit-row__fields">
                    <div className="ss-repo-edit-row__field">
                      <label className="ss-repo-edit-row__label">Integration</label>
                      <Select
                        value={row.projectIntegrationUuid || undefined}
                        onValueChange={(v) => handlePipelineIntegrationChange(row._key, 'build', v)}
                        placeholder={
                          pipelineIntegrationsLoading ? 'Loading…' : 'Select integration'
                        }
                        disabled={pipelineIntegrationsLoading}
                      >
                        {pipelineIntegrations.map((intg) => (
                          <SelectItem key={intg.uuid} value={intg.uuid}>
                            {formatIntegrationOptionLabel(intg)}
                          </SelectItem>
                        ))}
                      </Select>
                    </div>
                    <div className="ss-repo-edit-row__field">
                      <label className="ss-repo-edit-row__label">Pipeline</label>
                      <Select
                        key={`build-pipeline-sel-${row._key}-${row.projectIntegrationUuid}`}
                        value={row.providerResourceId || undefined}
                        onValueChange={(v) => handlePipelineChange(row._key, 'build', v)}
                        placeholder={
                          !row.projectIntegrationUuid
                            ? 'Select integration first'
                            : row.pipelinesLoading
                              ? 'Loading…'
                              : 'Select pipeline'
                        }
                        disabled={!row.projectIntegrationUuid || row.pipelinesLoading}
                      >
                        {row.pipelines.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </Select>
                    </div>
                  </div>
                  <div className="ss-repo-edit-row__meta">
                    <label className="ss-repo-edit-row__enabled-label">
                      <input
                        type="checkbox"
                        checked={row.enabled}
                        onChange={(e) => updatePipelineEnabled(row._key, 'build', e.target.checked)}
                        aria-label="Enable sync for this build pipeline"
                      />
                      Active
                    </label>
                    <Tooltip.Simple label="Remove">
                      <button
                        type="button"
                        className="ss-repo-edit-row__remove"
                        onClick={() => removePipelineRow(row._key, 'build')}
                        aria-label="Remove this build pipeline"
                      >
                        ✕
                      </button>
                    </Tooltip.Simple>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!editMode && settings.buildPipelines && settings.buildPipelines.length > 0 && (
            <div className="ss-settings-repo-cards">
              {settings.buildPipelines.map((p) => (
                <div
                  key={`${p.projectIntegrationUuid}-${p.providerResourceId}`}
                  className="ss-settings-repo-card"
                >
                  <div className="ss-meta-group">
                    <span className="ss-meta-label">Pipeline</span>
                    <Tooltip.Simple label={p.providerResourceName}>
                      <span className="ss-meta-value ss-settings-repo-card__name">
                        {p.providerResourceName}
                      </span>
                    </Tooltip.Simple>
                  </div>
                  <div className="ss-meta-group">
                    <span className="ss-meta-label">Status</span>
                    <span
                      className={`ss-settings-repo-row__status ${p.enabled ? 'ss-settings-repo-row__status--on' : 'ss-settings-repo-row__status--off'}`}
                    >
                      {p.enabled ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ── Release Pipelines section ── */}
      {sourceType === 'pipeline' && (
        <section
          className="ss-settings-view"
          aria-labelledby="ss-settings-release-pipelines-heading"
        >
          <div className="ss-settings-section__header-row">
            <h3 className="ss-settings-section__title" id="ss-settings-release-pipelines-heading">
              Release Pipelines
            </h3>
            {editMode && (
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => addNewPipelineRow('release')}
                aria-label="Add release pipeline"
              >
                Add
              </Button>
            )}
          </div>

          {editMode && releaseRows.length === 0 && (
            <p className="ss-settings-pane__hint">
              No release pipelines added. Click <strong>Add</strong> to add one.
            </p>
          )}

          {!editMode && (!settings.releasePipelines || settings.releasePipelines.length === 0) && (
            <p className="ss-settings-pane__hint">No release pipelines selected.</p>
          )}

          {editMode && releaseRows.length > 0 && (
            <div className="ss-repo-edit-list">
              {releaseRows.map((row) => (
                <div key={row._key} className="ss-repo-edit-row">
                  <div className="ss-repo-edit-row__fields">
                    <div className="ss-repo-edit-row__field">
                      <label className="ss-repo-edit-row__label">Integration</label>
                      <Select
                        value={row.projectIntegrationUuid || undefined}
                        onValueChange={(v) =>
                          handlePipelineIntegrationChange(row._key, 'release', v)
                        }
                        placeholder={
                          pipelineIntegrationsLoading ? 'Loading…' : 'Select integration'
                        }
                        disabled={pipelineIntegrationsLoading}
                      >
                        {pipelineIntegrations.map((intg) => (
                          <SelectItem key={intg.uuid} value={intg.uuid}>
                            {formatIntegrationOptionLabel(intg)}
                          </SelectItem>
                        ))}
                      </Select>
                    </div>
                    <div className="ss-repo-edit-row__field">
                      <label className="ss-repo-edit-row__label">Pipeline</label>
                      <Select
                        key={`release-pipeline-sel-${row._key}-${row.projectIntegrationUuid}`}
                        value={row.providerResourceId || undefined}
                        onValueChange={(v) => handlePipelineChange(row._key, 'release', v)}
                        placeholder={
                          !row.projectIntegrationUuid
                            ? 'Select integration first'
                            : row.pipelinesLoading
                              ? 'Loading…'
                              : 'Select pipeline'
                        }
                        disabled={!row.projectIntegrationUuid || row.pipelinesLoading}
                      >
                        {row.pipelines.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </Select>
                    </div>
                  </div>
                  <div className="ss-repo-edit-row__meta">
                    <label className="ss-repo-edit-row__enabled-label">
                      <input
                        type="checkbox"
                        checked={row.enabled}
                        onChange={(e) =>
                          updatePipelineEnabled(row._key, 'release', e.target.checked)
                        }
                        aria-label="Enable sync for this release pipeline"
                      />
                      Active
                    </label>
                    <Tooltip.Simple label="Remove">
                      <button
                        type="button"
                        className="ss-repo-edit-row__remove"
                        onClick={() => removePipelineRow(row._key, 'release')}
                        aria-label="Remove this release pipeline"
                      >
                        ✕
                      </button>
                    </Tooltip.Simple>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!editMode && settings.releasePipelines && settings.releasePipelines.length > 0 && (
            <div className="ss-settings-repo-cards">
              {settings.releasePipelines.map((p) => (
                <div
                  key={`${p.projectIntegrationUuid}-${p.providerResourceId}`}
                  className="ss-settings-repo-card"
                >
                  <div className="ss-meta-group">
                    <span className="ss-meta-label">Pipeline</span>
                    <Tooltip.Simple label={p.providerResourceName}>
                      <span className="ss-meta-value ss-settings-repo-card__name">
                        {p.providerResourceName}
                      </span>
                    </Tooltip.Simple>
                  </div>
                  <div className="ss-meta-group">
                    <span className="ss-meta-label">Status</span>
                    <span
                      className={`ss-settings-repo-row__status ${p.enabled ? 'ss-settings-repo-row__status--on' : 'ss-settings-repo-row__status--off'}`}
                    >
                      {p.enabled ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {(sourceType === 'repo' || sourceType === 'pipeline') && editMode && (
        <section className="ss-settings-view" aria-labelledby="ss-settings-schedule-heading">
          <div className="ss-settings-section__header-row">
            <h3 className="ss-settings-section__title" id="ss-settings-schedule-heading">
              Schedule
            </h3>
          </div>

          <div className="ss-settings-schedule-form">
            <label className="ss-repo-edit-row__enabled-label">
              <input
                type="checkbox"
                checked={editScheduleEnabled}
                onChange={(e) => setEditScheduleEnabled(e.target.checked)}
              />
              Enable scheduled sync
            </label>
            {editScheduleEnabled && (
              <div className="ss-settings-schedule-form__cron">
                <label className="ss-repo-edit-row__label" htmlFor="repo-schedule-cron">
                  Cron expression (UTC)
                </label>
                <input
                  id="repo-schedule-cron"
                  type="text"
                  className="ss-settings-schedule-form__input"
                  value={editScheduleCron}
                  onChange={(e) => setEditScheduleCron(e.target.value)}
                  placeholder="0 2 * * *"
                />
                <CronPreview cron={editScheduleCron} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Schedule category ── */}
      {(sourceType === 'repo' || sourceType === 'pipeline') && !editMode && (
        <section className="ss-settings-view" aria-labelledby="ss-settings-schedule-heading">
          <h3 className="ss-settings-section__title" id="ss-settings-schedule-heading">
            Schedule
          </h3>
          {settings.schedule.enabled && settings.schedule.scheduleCron ? (
            <CronPreview cron={settings.schedule.scheduleCron} />
          ) : (
            <span className="ss-meta-value ss-settings-pane__hint">No schedule configured</span>
          )}
        </section>
      )}

      {/* ── Edit mode actions ── */}
      {editMode && (
        <div className="ss-settings-pane__actions">
          <Button type="button" variant="ghost" size="sm" onClick={cancelEdit} disabled={saving}>
            <ArrowLeftIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
            Back
          </Button>
          <div className="ss-settings-pane__actions-end">
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void handleSave()}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});
